import React, { useState, useEffect } from 'react'
import { Button, Checkbox } from '../Common'
import { useNls } from '../../nls/nlsProvider'
import type { NlsKey } from '../../nls/strings_nls'
import {
  setHelperEduJobs,
  listEduTransportRoutes,
  type EduTransportRoute,
} from '../../services/eduHelperJobsService'
import {
  listSeriesForWorkspace,
  type EduSessionSeries,
} from '../../services/eduTimetableService'

export interface HelperSchoolJobsSectionProps {
  workspaceId: string
  helperUid: string
  isEduWorkspace: boolean
  canManage: boolean
  initialJobs?: ('ROLL' | 'GATE' | 'BUS' | 'DESK')[]
  initialRouteIds?: string[]
  initialRollSessionIds?: string[]
  onSaved?: () => void
}

type EduJobType = 'ROLL' | 'GATE' | 'BUS' | 'DESK'

export const HelperSchoolJobsSection: React.FC<HelperSchoolJobsSectionProps> = ({
  workspaceId,
  helperUid,
  isEduWorkspace,
  canManage,
  initialJobs = [],
  initialRouteIds = [],
  initialRollSessionIds = [],
  onSaved,
}) => {
  const { t } = useNls()

  const [selectedJobs, setSelectedJobs] = useState<Set<EduJobType>>(
    () => new Set(initialJobs),
  )
  const [selectedRouteIds, setSelectedRouteIds] = useState<Set<string>>(
    () => new Set(initialRouteIds),
  )
  const [selectedSessionIds, setSelectedSessionIds] = useState<Set<string>>(
    () => new Set(initialRollSessionIds),
  )

  const [routes, setRoutes] = useState<EduTransportRoute[]>([])
  const [routesLoading, setRoutesLoading] = useState(false)

  const [sessions, setSessions] = useState<EduSessionSeries[]>([])
  const [sessionsLoading, setSessionsLoading] = useState(false)

  const [saving, setSaving] = useState(false)
  const [statusMessage, setStatusMessage] = useState<string | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const initialJobsKey = (initialJobs || []).join(',')
  const initialRoutesKey = (initialRouteIds || []).join(',')
  const initialSessionsKey = (initialRollSessionIds || []).join(',')

  const prevKeyRef = React.useRef(`${initialJobsKey}:${initialRoutesKey}:${initialSessionsKey}`)
  useEffect(() => {
    const nextKey = `${initialJobsKey}:${initialRoutesKey}:${initialSessionsKey}`
    if (prevKeyRef.current !== nextKey) {
      prevKeyRef.current = nextKey
      setSelectedJobs(new Set(initialJobs))
      setSelectedRouteIds(new Set(initialRouteIds))
      setSelectedSessionIds(new Set(initialRollSessionIds))
    }
  }, [initialJobs, initialRouteIds, initialRollSessionIds, initialJobsKey, initialRoutesKey, initialSessionsKey])

  const hasBus = selectedJobs.has('BUS')
  const hasRoll = selectedJobs.has('ROLL')

  // Load routes when Bus job is selected
  useEffect(() => {
    if (!isEduWorkspace || !hasBus) return
    let active = true
    void Promise.resolve().then(() => {
      if (!active) return
      setRoutesLoading(true)
      return listEduTransportRoutes({ workspaceId })
        .then((res) => {
          if (!active) return
          setRoutes(res.routes || [])
        })
        .catch((err) => {
          if (!active) return
          console.warn('Failed to load transport routes', err)
        })
        .finally(() => {
          if (active) setRoutesLoading(false)
        })
    })
    return () => {
      active = false
    }
  }, [workspaceId, isEduWorkspace, hasBus])

  // Load sessions when Roll job is selected
  useEffect(() => {
    if (!isEduWorkspace || !hasRoll) return
    let active = true
    void Promise.resolve().then(() => {
      if (!active) return
      setSessionsLoading(true)
      return listSeriesForWorkspace(workspaceId)
        .then((res) => {
          if (!active) return
          setSessions(res || [])
        })
        .catch((err) => {
          if (!active) return
          console.warn('Failed to load class sessions', err)
        })
        .finally(() => {
          if (active) setSessionsLoading(false)
        })
    })
    return () => {
      active = false
    }
  }, [workspaceId, isEduWorkspace, hasRoll])

  if (!isEduWorkspace || !canManage) {
    return null
  }

  const toggleJob = (job: EduJobType) => {
    setSelectedJobs((prev) => {
      const next = new Set(prev)
      if (next.has(job)) {
        next.delete(job)
      } else {
        next.add(job)
      }
      return next
    })
  }

  const toggleRoute = (routeId: string) => {
    setSelectedRouteIds((prev) => {
      const next = new Set(prev)
      if (next.has(routeId)) {
        next.delete(routeId)
      } else {
        next.add(routeId)
      }
      return next
    })
  }

  const toggleSession = (sessionId: string) => {
    setSelectedSessionIds((prev) => {
      const next = new Set(prev)
      if (next.has(sessionId)) {
        next.delete(sessionId)
      } else {
        next.add(sessionId)
      }
      return next
    })
  }

  const handleSave = async () => {
    setSaving(true)
    setStatusMessage(null)
    setErrorMessage(null)
    try {
      await setHelperEduJobs({
        workspaceId,
        targetUid: helperUid,
        eduJobs: Array.from(selectedJobs),
        eduRouteIds: selectedJobs.has('BUS') ? Array.from(selectedRouteIds) : [],
        eduRollSessionIds: selectedJobs.has('ROLL') ? Array.from(selectedSessionIds) : [],
      })
      setStatusMessage(t('NLSInvitesHelperSchoolJobsSuccess'))
      onSaved?.()
    } catch (err: unknown) {
      setErrorMessage(
        err instanceof Error ? err.message : t('NLSInvitesHelperSchoolJobsError'),
      )
    } finally {
      setSaving(false)
    }
  }

  const jobOptions: { id: EduJobType; labelKey: NlsKey }[] = [
    { id: 'ROLL', labelKey: 'NLSInvitesHelperJobRoll' },
    { id: 'GATE', labelKey: 'NLSInvitesHelperJobGate' },
    { id: 'BUS', labelKey: 'NLSInvitesHelperJobBus' },
    { id: 'DESK', labelKey: 'NLSInvitesHelperJobDesk' },
  ]

  return (
    <section className="invitesSidecarSection" data-testid="helper-school-jobs-section">
      <h4>{t('NLSInvitesHelperSchoolJobsTitle')}</h4>
      <p className="mb-3 text-xs text-[var(--muted)]">
        {t('NLSInvitesHelperSchoolJobsBody')}
      </p>

      {statusMessage && (
        <div className="mb-2 rounded border border-[color-mix(in_srgb,var(--success)_40%,transparent)] bg-[color-mix(in_srgb,var(--success)_10%,transparent)] p-2 text-xs font-semibold text-[var(--text-strong)]">
          {statusMessage}
        </div>
      )}
      {errorMessage && (
        <div className="mb-2 rounded border border-[color-mix(in_srgb,var(--danger)_40%,transparent)] bg-[color-mix(in_srgb,var(--danger)_10%,transparent)] p-2 text-xs font-semibold text-[color-mix(in_srgb,var(--danger)_80%,var(--text-strong))]">
          {errorMessage}
        </div>
      )}

      <div className="mb-3 grid gap-2">
        {jobOptions.map((opt) => (
          <Checkbox
            key={opt.id}
            checked={selectedJobs.has(opt.id)}
            onChange={() => toggleJob(opt.id)}
            label={t(opt.labelKey)}
            wrapperClassName="flex items-center gap-2 text-xs text-[var(--text-strong)]"
          />
        ))}
      </div>

      {selectedJobs.has('BUS') && (
        <div className="mb-3 pl-3 border-l-2 border-[var(--border)]">
          <h5 className="mb-1 text-xs font-semibold text-[var(--text-strong)]">
            {t('NLSInvitesHelperRoutePickerLabel')}
          </h5>
          {routesLoading ? (
            <p className="text-xs text-[var(--muted)]">Loading routes...</p>
          ) : routes.length === 0 ? (
            <p className="text-xs text-[var(--muted)]">
              {t('NLSInvitesHelperRoutePickerEmpty')}
            </p>
          ) : (
            <div className="grid gap-1.5 max-h-36 overflow-y-auto pr-1">
              {routes.map((route) => (
                <Checkbox
                  key={route.id}
                  checked={selectedRouteIds.has(route.id)}
                  onChange={() => toggleRoute(route.id)}
                  label={route.name || route.code || route.id}
                  wrapperClassName="flex items-center gap-2 text-xs text-[var(--text)]"
                />
              ))}
            </div>
          )}
        </div>
      )}

      {selectedJobs.has('ROLL') && (
        <div className="mb-3 pl-3 border-l-2 border-[var(--border)]">
          <h5 className="mb-1 text-xs font-semibold text-[var(--text-strong)]">
            {t('NLSInvitesHelperSessionPickerLabel')}
          </h5>
          {sessionsLoading ? (
            <p className="text-xs text-[var(--muted)]">Loading sessions...</p>
          ) : sessions.length === 0 ? (
            <p className="text-xs text-[var(--muted)]">
              {t('NLSInvitesHelperSessionPickerEmpty')}
            </p>
          ) : (
            <div className="grid gap-1.5 max-h-36 overflow-y-auto pr-1">
              {sessions.map((series) => {
                const label = series.id || `${series.classId} · ${series.startTime}`
                return (
                  <Checkbox
                    key={series.id}
                    checked={series.id ? selectedSessionIds.has(series.id) : false}
                    onChange={() => series.id && toggleSession(series.id)}
                    label={label}
                    wrapperClassName="flex items-center gap-2 text-xs text-[var(--text)]"
                  />
                )
              })}
            </div>
          )}
        </div>
      )}

      <div className="mt-2 flex justify-end">
        <Button
          variant="secondary"
          size="sm"
          disabled={saving}
          onClick={handleSave}
        >
          {saving
            ? t('NLSInvitesHelperSchoolJobsSaving')
            : t('NLSInvitesHelperSchoolJobsSaveBtn')}
        </Button>
      </div>
    </section>
  )
}
